/**
 * Portal item links (course, web page, etc.) use paths relative to the portal basename
 * (/portal/:orgId/:portalId). New tabs need an absolute URL; same-tab navigation should use
 * React Router when the host passes onPortalRelativeNavigate.
 */
/** Match /portal/:orgId/:portalId — aligns with BrowserRouter basename. */
export function getPortalBasePath() {
    if (typeof window === 'undefined')
        return '';
    return window.location.pathname.match(/^\/portal\/[\w-]+\/[\w-]+/)?.[0] || '';
}
/** Strip a leading slash for react-router navigate(), which expects paths relative to basename. */
export function normalizePortalRelativePath(url) {
    if (url === undefined || url === null)
        return '';
    const t = String(url).trim();
    if (!t)
        return '';
    return t.replace(/^\//, '');
}
/**
 * Path for React Router `navigate()` relative to the portal basename.
 * Item references often include full same-origin URLs; passing those to `navigate()` causes a full page load.
 * Returns '' for external origins or paths that are not under the portal root.
 */
export function toPortalRouterRelativePath(itemUrl, rootPath) {
    const trimmed = String(itemUrl ?? '').trim();
    if (!trimmed)
        return '';
    if (!/^https?:\/\//i.test(trimmed)) {
        return normalizePortalRelativePath(trimmed);
    }
    if (typeof window === 'undefined') {
        return '';
    }
    try {
        const u = new URL(trimmed);
        if (u.origin !== window.location.origin) {
            return '';
        }
        const pathname = u.pathname;
        const suffix = (u.search || '') + (u.hash || '');
        const base = rootPath.endsWith('/') ? rootPath.slice(0, -1) : rootPath;
        if (base && (pathname === base || pathname.startsWith(`${base}/`))) {
            const rest = pathname.slice(base.length);
            return normalizePortalRelativePath(`${rest}${suffix}`);
        }
        const m = pathname.match(/^(\/portal\/[\w-]+\/[\w-]+)(\/.*)?$/);
        if (m) {
            const afterRoot = m[2] || '';
            return normalizePortalRelativePath(`${afterRoot}${suffix}`);
        }
        return '';
    }
    catch {
        return '';
    }
}
/**
 * Absolute URL for opening in a new window/tab. If `itemPath` is already http(s), returns as-is.
 */
export function buildPortalAbsoluteHref(rootPath, itemPath) {
    if (itemPath === undefined || itemPath === null) {
        return typeof window !== 'undefined' ? `${window.location.origin}${rootPath || '/'}` : '';
    }
    const path = String(itemPath).trim();
    if (!path) {
        return typeof window !== 'undefined' ? `${window.location.origin}${rootPath || '/'}` : '';
    }
    if (/^https?:\/\//i.test(path)) {
        return path;
    }
    const rel = path.startsWith('/') ? path : `/${path}`;
    const base = rootPath.endsWith('/') ? rootPath.slice(0, -1) : rootPath;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!base) {
        return `${origin}${rel}`;
    }
    return `${origin}${base}${rel}`;
}
/** First path segment for portal routes (basename-relative), aligned with PortalRouteMapper / linkNavigator. */
const ENTITY_TYPE_TO_PATH = {
    course: 'course',
    document: 'document',
    microcast: 'microcast',
    program: 'program',
    meeting: 'meeting',
    instructor: 'instructor',
    cat: 'cat',
    category: 'cat',
};
/**
 * When `url` is missing, build `/{segment}/{slugOrId}` for non-product portal entities.
 * Prefers `slug` for the last path segment when present; otherwise uses `id` / `itemID`.
 * Requires `organizationID` and at least one of slug or entity id.
 * Product / Shopify links must keep an explicit `url`.
 */
export function derivePortalItemRelativePath(item) {
    const t = String(item.type || '').toLowerCase();
    if (!t || t === 'product' || t === 'portal') {
        return null;
    }
    const org = item.organizationID != null ? String(item.organizationID).trim() : '';
    if (!org) {
        return null;
    }
    const slugTrim = item.slug != null && String(item.slug).trim() !== ''
        ? String(item.slug).trim()
        : '';
    const entityId = [item.id, item.itemID].find((x) => x != null && String(x).trim() !== '');
    const idStr = entityId != null ? String(entityId).trim() : '';
    if (!slugTrim && !idStr) {
        return null;
    }
    const slugPart = slugTrim || idStr;
    const segment = ENTITY_TYPE_TO_PATH[t];
    if (!segment) {
        return null;
    }
    return `${segment}/${slugPart}`;
}
/**
 * Rewrites `/{segment}/…` to use `slug` as the entity segment (e.g. course/my-slug vs course/uuid).
 * No-op when `slug` is empty or the path does not contain the entity segment.
 */
export function preferSlugInItemReferenceUrl(url, entityType, slug) {
    const s = slug != null ? String(slug).trim() : '';
    const u = url != null ? String(url).trim() : '';
    if (!s || !u) {
        return u;
    }
    const seg = ENTITY_TYPE_TO_PATH[String(entityType || '').toLowerCase()];
    if (!seg) {
        return u;
    }
    const re = new RegExp(`(${seg}/)([^/?#]+)`, 'i');
    if (!re.test(u)) {
        return u;
    }
    return u.replace(re, `$1${s}`);
}
/**
 * Prefer explicit `url` (with slug replacing id in path when `slug` is set); otherwise derive from type + ids.
 */
export function resolvePortalItemRelativeUrl(item) {
    const rawUrl = item.url != null ? String(item.url).trim() : '';
    const withSlug = preferSlugInItemReferenceUrl(rawUrl, item.type, item.slug);
    if (withSlug && /^https?:\/\//i.test(withSlug)) {
        return withSlug;
    }
    const explicit = normalizePortalRelativePath(withSlug);
    if (explicit) {
        return explicit;
    }
    return derivePortalItemRelativePath(item) || '';
}
//# sourceMappingURL=portalNavigation.js.map